import { Component, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges } from '@angular/core';
import { DocumentItem, DocumentService } from '@app/services/document.service';
import { ModalService } from '@app/core/services/modal.service';
import { ProductItem, ProductService, ProductUnitItem } from '@app/services/product.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { ComponentGet, ComponentService, GetComponentParams } from '@app/services/component.service';

@Component({
    selector: 'tt-component',
    templateUrl: './component.component.html',
    styleUrls: ['./component.component.css'],
    standalone: false,
})
export class ComponentComponent implements OnInit, OnChanges {
    @Input()
    public params?: GetComponentParams;

    public get?: ComponentGet;

    public productUnits: ProductUnitItem[] = [];

    public documents: DocumentItem[] = [];

    public saving = false;

    public deleting = false;

    constructor(
        private modal: ModalService,
        private component: ComponentService,
        private product: ProductService,
        private document: DocumentService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public searchProducts = (params?: { value?: string }): Promise<ProductItem[]> => {
        return this.product.getProducts({ prod_name: params?.value ?? this.get?.product_search ?? '', webpage_name: 'component', parm1: this.params?.prod_id, parm2: this.params?.comp_keyno, supplier_no: '0' });
    };

    public async onProductSelected(event?: ProductItem) {
        if (!this.get) return;

        this.get.comp_is_prod_id = event?.item_id ?? '';
        this.get.product_search = event?.item_name ?? '';

        if (!!event) {
            await this.updateComponentProduct();
        } else {
            this.get.stock_unit_no = '';
            this.get.input_unit_no = '';
            this.get.product_search = '';
            this.get.comp_prod_name = '';
            this.productUnits = [];
        }
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;

            const response = await this.component.saveComponent(this.get);

            if ('' + response?.errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: response.errormessage });
            } else {
                this.navigator?.back();
            }
        } finally {
            this.saving = false;
        }
    }

    public async deleteComponent() {
        if (!this.get) return;

        try {
            this.deleting = true;

            const confirmed = await this.modal.openConfirmDialog({ type: 'danger', title: 'confirm_title', message: 'confirm_message', ok: 'confirm_ok_label', cancel: 'confirm_cancel_label' });

            if (!confirmed) return;

            await this.component.deleteComponent(this.get);
            this.navigator?.back();
        } finally {
            this.deleting = false;
        }
    }

    private async loadGet() {
        if (!!this.params) {
            this.get = await this.component.getComponent(this.params);
        }
    }

    private async loadProductUnits() {
        if (!!this.get && this.get.comp_is_prod_id > '') {
            this.productUnits = await this.product.getProductUnits({ prod_id: this.get.comp_is_prod_id });
        }
    }

    private async loadDocuments() {
        if (!!this.get) {
            this.documents = await this.document.getDocumentsList({ argtype: 'prod_id', argvalue: this.get.prod_id, searchtext: '', doctype: 'd', onlyFilenames: '0', glyphicon_select: '1' });
        }
    }

    private async updateComponentProduct() {
        if (!!this.get) {
            const { comp_is_prod_id, comp_prod_name, waste_pst, url_picture, stock_unit_no } = await this.component.updateComponentProduct({ comp_is_prod_id: this.get.comp_is_prod_id, prod_id: this.get.prod_id });

            this.get.comp_is_prod_id = comp_is_prod_id;
            this.get.comp_prod_name = comp_prod_name;
            this.get.comp_waste_pst = waste_pst;
            this.get.url_picture = url_picture;

            if (this.get.comp_is_prod_id > '') {
                this.get.stock_unit_no = stock_unit_no;
                this.get.input_unit_no = stock_unit_no;
                await this.loadProductUnits();
            }
        }
    }

    private async init() {
        if (!this.params) return;

        await this.loadGet();
        this.navigator?.setHeading?.(this.get?.page_heading ?? '');
        await Promise.all([this.loadProductUnits(), this.loadDocuments()]);

        if ((this.get?.prod_id_new ?? '') > '') {
            await this.updateComponentProduct();
        }
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.params = this.navigator.params as unknown as GetComponentParams;
            this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!!changes['params']?.currentValue) {
            this.init();
        }
    }
}
